import {
  type EpisodeEndReason,
  type EpisodeOfCare,
  type Observation,
  type Patient,
  type PatientRecord,
  SYS_PROGRAMME,
  type ServiceRequest,
  closeEpisode,
  conceptOf,
  currentRegimen,
  isDeceased,
  isLabOrder,
  isRemovedPatient,
  motherOf,
  nextVisitOf,
  observationValue,
  removeEpisode,
  splitRecord,
} from '@assist/core';
import { useEffect, useState } from 'preact/hooks';
import { FollowupCard } from '../components/FollowupCard.js';
import { LabOrdersCard } from '../components/LabOrdersCard.js';
import { ReferralsCard } from '../components/ReferralsCard.js';
import { RemovePatient, RemovedBanner, actorName } from '../components/Removal.js';
import { SeriesCard } from '../components/SeriesCard.js';
import { app, clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';
import { ageOf, birthLabel, displayName, mrnOf } from '../patient.js';
import {
  type AvailableForm,
  HubTile,
  PatientChrome,
  formLabel,
  formsFor,
  stepForms,
} from '../shell.js';
import { packIcon } from '../ui/icons.js';

export function PatientView({ id }: { id: string }) {
  const a = useApp();
  const [p, setP] = useState<Patient | null | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [record, setRecord] = useState<PatientRecord | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh when sync status changes
  useEffect(() => {
    void app.session?.get<Patient>('Patient', id).then(setP);
    void app.session?.forPatient(id).then((rs) => setRecord(splitRecord(rs)));
    void app.session?.db.records
      .get(`Patient/${id}`)
      .then((r) => setPending(!!r && r.serverVersion === null));
  }, [id, a.status]);
  if (p === undefined) return null;
  if (p === null) return <p class="muted">Not found on this device.</p>;
  const age = ageOf(p, clock.today());
  const born = birthLabel(p);
  const removed = isRemovedPatient(p);
  const rows: Array<[string, string | undefined]> = [
    [
      t.sex,
      p.gender === 'female'
        ? t.female
        : p.gender === 'male'
          ? t.male
          : p.gender === 'other'
            ? t.otherSex
            : t.sexNotRecorded,
    ],
    [t.ageLabel, age ? t.age(age) : undefined],
    [t.birthDate, born ?? undefined],
    [t.mrn, mrnOf(p)],
    [t.phone, p.telecom?.find((x) => x.system === 'phone')?.value],
    [t.kebele, p.address?.[0]?.line?.[0]],
    [t.woreda, p.address?.[0]?.district],
  ];
  const packs = activePacks(a.packSettings);
  const forms = formsFor(packs, p, record).filter((f) => !f.done);
  const inEpisode = stepForms(forms, record);
  const others = forms.filter((f) => !inEpisode.includes(f));
  const tile = (f: AvailableForm) => (
    <HubTile
      key={`${f.pack.id}/${f.form.id}`}
      href={f.href}
      icon={f.form.icon ?? packIcon(f.pack.id)}
      label={formLabel(f.pack, f.form)}
    />
  );
  const episodic = new Set(
    packs.flatMap(({ pack }) => pack.programmes.filter((x) => x.episode).map((x) => x.code)),
  );
  const chronicActive = record?.episodes.some(
    (e) =>
      e.status === 'active' &&
      !e.type.some((x) =>
        x.coding?.some((c) => c.system === SYS_PROGRAMME && episodic.has(c.code ?? '')),
      ),
  );
  const jump = (card: string) =>
    document.querySelector(`[data-card="${card}"]`)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <section>
      <PatientChrome id={id} here="hub" />
      <div class="card">
        <h3>
          {displayName(p)}
          {pending ? <span class="pill amber">{t.notOnServer}</span> : null}
          {isDeceased(p) ? <span class="pill red">{t.deceased}</span> : null}
          {removed ? <span class="pill amber">{t.removedPill}</span> : null}
        </h3>
        <RemovedBanner record={p} />
        <div class="rec">
          <div class="rec-g">
            {rows
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div class="rec-f" key={k}>
                  <span class="rec-l">{k}</span>
                  <span class="rec-v">{v}</span>
                </div>
              ))}
            {motherOf(p) ? <MotherLink id={motherOf(p) as string} /> : null}
          </div>
        </div>
        <div class="hubgrid">
          {isDeceased(p) || removed ? null : inEpisode.map(tile)}
          {chronicActive ? (
            <HubTile icon="worklists" label={t.followupCard} onClick={() => jump('followup')} />
          ) : null}
          {record?.referrals.some((r) => !isLabOrder(r)) ? (
            <HubTile icon="referral" label={t.referrals} onClick={() => jump('referrals')} />
          ) : null}
          <HubTile icon="record" label={t.visits} onClick={() => jump('visits')} />
          <HubTile href={`#/patient/${id}/record`} icon="record" label={t.fullRecord} />
          <HubTile href={`#/patient/${id}/edit`} icon="edit" label={t.editDetails} />
        </div>
        {!isDeceased(p) &&
        !removed &&
        others.length &&
        inEpisode.some((f) => [f.form.requires ?? []].flat().length) ? (
          <>
            <div class="tsect hubsect">{t.otherServices}</div>
            <div class="hubgrid">{others.map(tile)}</div>
          </>
        ) : null}
      </div>
      {record
        ? packs.flatMap(({ pack, allowDraft }) =>
            pack.forms.map((f) =>
              f.series ? (
                <SeriesCard
                  key={`${pack.id}/${f.id}`}
                  pack={pack}
                  form={{ ...f, series: f.series }}
                  patient={p}
                  record={record}
                  allowDraft={allowDraft}
                />
              ) : null,
            ),
          )
        : null}
      <Babies mother={p} record={record} />
      <Care patient={p} record={record} />
      <RemovePatient patient={p} />
    </section>
  );
}

/** Programme enrolments, current medicines and past visits, from this device's record. */
function Care({
  patient,
  record,
}: {
  patient: Patient;
  record: PatientRecord | null;
}) {
  const a = useApp();
  const packs = activePacks(a.packSettings);
  if (!record) return null;
  const episodes = record.episodes.filter((e) => e.status === 'active');
  const episodeCodes = new Set(
    packs.flatMap((x) => x.pack.programmes.filter((p) => p.episode).map((p) => p.code)),
  );
  const drugs = new Map(packs.flatMap((p) => p.pack.drugs.map((d) => [d.code, d] as const)));
  const concepts = new Map(packs.flatMap((p) => p.pack.concepts.map((c) => [c.code, c] as const)));
  const regimen = currentRegimen(record.medications);
  const obsByEnc = new Map<string, Observation[]>();
  for (const o of record.observations) {
    const k = o.encounter?.reference ?? '';
    obsByEnc.set(k, [...(obsByEnc.get(k) ?? []), o]);
  }
  const summary = (encId: string) => {
    const obs = obsByEnc.get(`Encounter/${encId}`) ?? [];
    const v = (code: string) => {
      const o = obs.find((x) => conceptOf(x) === code);
      return o ? observationValue(o) : null;
    };
    const parts: string[] = [];
    if (v('sbp') !== null && v('dbp') !== null) parts.push(`BP ${v('sbp')}/${v('dbp')}`);
    for (const code of ['cervix_cm', 'fhr_baseline', 'fbs', 'rbs', 'hba1c', 'weight']) {
      const x = v(code);
      const c = concepts.get(code);
      if (x !== null && c) parts.push(`${c.display} ${x}${c.unit ? ` ${c.unit}` : ''}`);
    }
    return parts.join(' · ');
  };
  return (
    <>
      <div class="card" data-card="programmes">
        <h3>{t.programmes}</h3>
        {episodes.length ? (
          <ul class="plain">
            {episodes.map((e) => {
              const c = e.type
                .flatMap((x) => x.coding ?? [])
                .find((x) => x.system === SYS_PROGRAMME);
              return (
                <li key={e.id}>
                  <strong>{c?.display ?? c?.code}</strong>{' '}
                  {e.period?.start ? (
                    <span class="muted">{t.since(eth(e.period.start))}</span>
                  ) : null}
                  {episodeCodes.has(c?.code ?? '') ? (
                    <CloseEpisode episode={e} patient={patient} />
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p class="muted">{t.noProgrammes}</p>
        )}
        {packs.length ? null : <p class="muted">{t.noPacks}</p>}
      </div>
      <FollowupCard
        patient={patient}
        record={record}
        programmes={packs.flatMap((x) => x.pack.programmes)}
        onSaved={() => undefined}
      />
      <ReferralsCard referrals={openRefs(record.referrals)} onSaved={() => undefined} />
      <LabOrdersCard orders={record.referrals} />
      {Object.keys(regimen).length ? (
        <div class="card" data-card="medicines">
          <h3>{t.medicines}</h3>
          <ul class="plain">
            {Object.entries(regimen).map(([code, r]) => {
              const d = drugs.get(code);
              const dose = Math.round(r.dose * 100) / 100;
              return (
                <li key={code}>
                  {d?.display ?? code} {dose} {d?.unit ?? ''} {t.dosing(r.timesPerDay)}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <div class="card" data-card="visits">
        <h3>{t.visits}</h3>
        {record.encounters.length ? (
          <ul class="plain visits">
            {record.encounters.map((e) => {
              const next = nextVisitOf(e);
              return (
                <li key={e.id}>
                  <strong>{eth(e.period.start)}</strong>{' '}
                  {e.type
                    ?.map((x) => x.coding?.[0]?.display)
                    .filter(Boolean)
                    .join(', ')}
                  <div class="muted">
                    {summary(e.id)}
                    {next ? ` · ${t.nextVisit}: ${eth(next)}` : ''}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p class="muted">{t.noVisits}</p>
        )}
      </div>
    </>
  );
}

const END_REASONS: EpisodeEndReason[] = [
  'completed',
  'referred',
  'transferred-out',
  'died',
  'entered-in-error',
];

/** Close an episode of care (a labour that has ended); the record stays. */
function CloseEpisode({ episode, patient }: { episode: EpisodeOfCare; patient: Patient }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<EpisodeEndReason | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    const s = app.session;
    if (!s || !reason) return;
    setBusy(true);
    try {
      await s.save(
        reason === 'entered-in-error'
          ? removeEpisode(episode, {
              reason: t.endReasons['entered-in-error'] ?? 'Opened in error',
              by: actorName(),
              at: clock.now(),
            })
          : closeEpisode(episode, reason, clock.today()),
      );
      if (reason === 'died') await s.save({ ...patient, deceasedBoolean: true } as Patient);
      app.saved();
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  if (!open)
    return (
      <div class="actions left">
        <button type="button" class="sec small" onClick={() => setOpen(true)}>
          {t.closeEpisode}
        </button>
        <span class="muted">{t.closeEpisodeHelp}</span>
      </div>
    );
  return (
    <fieldset class="askbox" aria-label={t.closeEpisode}>
      <div class="ask-h">
        <b>{t.closeEpisode}</b>
      </div>
      <p class="ask-note">{t.closeEpisodeHelp}</p>
      <div class="ask-chips">
        {END_REASONS.map((x) => (
          <button
            key={x}
            type="button"
            class={reason === x ? 'ask-chip on' : 'ask-chip'}
            aria-pressed={reason === x}
            onClick={() => setReason(x)}
          >
            {t.endReasons[x]}
          </button>
        ))}
      </div>
      {reason === 'died' ? <div class="alert">{t.diedWarning}</div> : null}
      {err ? <p class="ask-err">{err}</p> : null}
      <div class="ask-acts">
        <button type="button" class="act" disabled={!reason || busy} onClick={() => void save()}>
          {t.closeEpisodeSave}
        </button>
        <button type="button" class="sec" onClick={() => setOpen(false)}>
          {t.cancel}
        </button>
      </div>
    </fieldset>
  );
}

/** Her babies (linked records), and registering a newborn while she is in a birth episode. */
/** A baby's record names the mother, with a way back to her record. */
function MotherLink({ id }: { id: string }) {
  const [m, setM] = useState<Patient | null>(null);
  useEffect(() => {
    void app.session?.get<Patient>('Patient', id).then((x) => setM(x ?? null));
  }, [id]);
  return (
    <div class="rec-f">
      <span class="rec-l">{t.motherLabel}</span>
      <span class="rec-v">
        <a href={`#/patient/${id}`}>{m ? displayName(m) : t.motherLabel}</a>
      </span>
    </div>
  );
}

function Babies({ mother, record }: { mother: Patient; record: PatientRecord | null }) {
  const a = useApp();
  const [babies, setBabies] = useState<Patient[]>([]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh when the store changes
  useEffect(() => {
    void app.session
      ?.list<Patient>('Patient')
      .then((ps) => setBabies(ps.filter((x) => motherOf(x) === mother.id && !isRemovedPatient(x))));
  }, [mother.id, a.status]);
  const births = new Set(
    activePacks(a.packSettings).flatMap(({ pack }) =>
      pack.programmes.filter((x) => x.births).map((x) => x.code),
    ),
  );
  const inBirthEpisode = (record?.episodes ?? []).some(
    (e) =>
      e.status === 'active' &&
      e.type.some((x) =>
        x.coding?.some((c) => c.system === SYS_PROGRAMME && births.has(c.code ?? '')),
      ),
  );
  if (mother.gender !== 'female' || (!babies.length && !inBirthEpisode)) return null;
  return (
    <div class="card" data-card="babies">
      <h3>{t.herBabies}</h3>
      {babies.length ? (
        <ul class="plist">
          {babies.map((b) => (
            <li key={b.id}>
              <a href={`#/patient/${b.id}`}>
                <span class="pav">
                  {b.gender === 'male' ? '♂' : b.gender === 'female' ? '♀' : '·'}
                </span>
                <span class="pinfo">
                  <span class="pnm">{displayName(b)}</span>
                  <span class="pmeta">
                    {b.birthDate ? `${t.born} ${eth(b.birthDate)}` : ''}
                    {isDeceased(b) ? ` · ${t.deceased}` : ''}
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p class="muted">{t.noBabies}</p>
      )}
      {inBirthEpisode ? (
        <div class="actions left">
          <a class="sec" href={`#/patient/${mother.id}/newborn`}>
            + {t.registerNewborn}
          </a>
        </div>
      ) : null}
    </div>
  );
}

const openRefs = (srs: ServiceRequest[]) => srs.filter((s) => !isLabOrder(s));
